// v19.40 — Post-purchase success + downloads page.
// Route: /purchase/success/:purchaseId?token=…
// Loaded via the link in the buyer's email OR the direct redirect from
// Stripe (via /purchase/success/pending?stripe_session_id=…). First
// screen after purchase — plays the delivered audio in-page, exposes
// download buttons, shows the license PDF, and upsells the beat
// generator in the same style.
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { DownloadSimple, CheckCircle, FileText, Sparkle, ArrowRight, Play, Pause, Warning } from "@phosphor-icons/react";
import API from "../api";
import GlobalNav from "../components/GlobalNav";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// v19.40 — Inline audio preview for each untagged file. Uses the same
// signed URL as the Download button but passes ?stream=1 so the
// backend serves the file inline with a proper audio MIME type. If
// the browser fails to load the file we surface a "Try again" affordance
// instead of a silent broken state.
function DownloadRow({ d }) {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const isAudio = d.kind === "untagged_mp3" || d.kind === "untagged_wav";
  const streamHref = d.stream_url ? `${API_URL}${d.stream_url}` : null;
  const downloadHref = `${API_URL}${d.url}`;

  const togglePlay = async () => {
    if (!audioRef.current) audioRef.current = new Audio();
    const a = audioRef.current;
    if (playing) { a.pause(); setPlaying(false); return; }
    setErr("");
    setLoading(true);
    a.src = streamHref;
    a.onended = () => setPlaying(false);
    a.onerror = () => { setErr("This file could not be played. Use the Download button to save it."); setPlaying(false); setLoading(false); };
    a.oncanplay = () => setLoading(false);
    try {
      await a.play();
      setPlaying(true);
    } catch (e) {
      setErr(`Playback error: ${e?.message || "unknown"}`);
      setLoading(false);
    }
  };

  return (
    <div
      className={`rounded-xl border px-3 py-2.5 flex items-center gap-3 ${
        d.kind === "license"
          ? "border-white/10 bg-black/30"
          : "border-emerald-400/50 bg-emerald-500/10"
      }`}
      data-testid={`download-row-${d.kind}`}
    >
      {isAudio && streamHref ? (
        <button
          onClick={togglePlay}
          disabled={loading}
          data-testid={`preview-play-${d.kind}`}
          className="w-9 h-9 shrink-0 rounded-full flex items-center justify-center bg-emerald-500 hover:bg-emerald-400 text-black disabled:opacity-50"
          aria-label={playing ? "Pause" : "Play"}
        >
          {playing ? <Pause size={16} weight="fill" /> : <Play size={16} weight="fill" />}
        </button>
      ) : (
        <div className="w-9 h-9 shrink-0 rounded-full flex items-center justify-center bg-white/5 border border-white/10">
          <FileText size={16} weight="bold" className="text-white/70" />
        </div>
      )}
      <div className="min-w-0 flex-1">
        <div className="font-bold text-sm truncate">{d.label}</div>
        {err ? (
          <div className="mt-0.5 text-[11px] text-red-300 flex items-center gap-1"><Warning size={11} weight="bold" /> {err}</div>
        ) : (
          <div className="mt-0.5 text-[11px] text-white/45">{isAudio ? "Preview above · save with Download" : "PDF · save with Download"}</div>
        )}
      </div>
      <a
        href={downloadHref}
        className="shrink-0 px-3 py-1.5 rounded-full text-xs font-black bg-emerald-500 hover:bg-emerald-400 text-black flex items-center gap-1"
        data-testid={`download-${d.kind}`}
        download
      >
        <DownloadSimple size={14} weight="bold" /> Download
      </a>
    </div>
  );
}

export default function PurchaseSuccessPage() {
  const { purchaseId } = useParams();
  const [params] = useSearchParams();
  const nav = useNavigate();
  const token = params.get("token") || "";
  const [purchase, setPurchase] = useState(null);
  const [beat, setBeat] = useState(null);
  const [downloads, setDownloads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [pendingHint, setPendingHint] = useState("Confirming your payment with Stripe…");

  // v19.40 — /purchase/success/pending?stripe_session_id=cs_…
  // is the Stripe redirect target. Poll every 1.5s (up to 90s) for
  // the webhook to fulfill the sale, then swap the URL to the real
  // /purchase/success/{id} route.
  useEffect(() => {
    if (purchaseId !== "pending") return;
    const sessionId = params.get("stripe_session_id");
    if (!sessionId) { setErr("Missing Stripe session — please return to the beat page and try again."); setLoading(false); return; }
    let cancelled = false;
    let attempts = 0;
    // v19.41 — Track whether Stripe has confirmed the payment yet.
    // If the poller sits >20 attempts (~30s) without a `paid` status
    // we treat it as an abandoned/unpaid checkout and STOP with a
    // clear error — never a false "your payment succeeded" message.
    let seenPaid = false;
    const tick = async () => {
      if (cancelled) return;
      attempts += 1;
      if (attempts === 5 && seenPaid) setPendingHint("Building your license PDF…");
      if (attempts === 12 && seenPaid) setPendingHint("Almost there — securing your download links…");
      try {
        const { data } = await API.get(`/store/purchases/by-session/${encodeURIComponent(sessionId)}`);
        if (data.ready) {
          nav(`/purchase/success/${data.purchase_id}?token=${encodeURIComponent(data.download_token)}`, { replace: true });
          return;
        }
        // v19.41 — Stripe confirmed the session ended without paying
        // (blocked=true for terminal non-paid states). Stop the
        // walkthrough immediately with a clear error.
        if (data.blocked) {
          setErr(`Payment not confirmed by Stripe (status: ${data.payment_status || "unknown"}). No charge has been made and no license has been issued. Please return to the beat page and try again — if you were charged, forward your Stripe receipt to support@crowdaw.com.`);
          setLoading(false);
          return;
        }
        // Paid — webhook hasn't caught up yet. Keep polling with the
        // walkthrough hints so the buyer sees progress.
        if (data.awaiting_webhook || data.payment_status === "paid") {
          seenPaid = true;
          setPendingHint("Payment confirmed — finalising your license…");
        }
      } catch (e) {
        // Backend refused to identify the session (400 invalid, 404
        // unknown). Fail loudly rather than pretend to succeed.
        const detail = e?.response?.data?.detail;
        if (detail) {
          setErr(detail);
          setLoading(false);
          return;
        }
        /* transient — keep polling */
      }
      // v19.41 — If we've polled for ~30s and Stripe still hasn't
      // seen the payment, treat the checkout as abandoned and stop.
      if (!seenPaid && attempts > 20) {
        setErr("Stripe never confirmed a completed payment for this session. No charge has been made and no license has been issued. Please return to the beat page and try again — if you were charged, forward your Stripe receipt to support@crowdaw.com.");
        setLoading(false);
        return;
      }
      if (attempts > 60) {
        setErr("Your payment succeeded but delivery is taking longer than usual. Your download link is on its way to your inbox — check spam if it isn't there within 5 minutes.");
        setLoading(false);
        return;
      }
      setTimeout(tick, 1500);
    };
    tick();
    return () => { cancelled = true; };
  }, [purchaseId, params, nav]);

  useEffect(() => {
    if (purchaseId === "pending") return;
    if (!token) { setErr("Missing token"); setLoading(false); return; }
    (async () => {
      try {
        const { data } = await API.get(`/store/purchases/${purchaseId}?token=${encodeURIComponent(token)}`);
        setPurchase(data.purchase);
        setBeat(data.beat);
        setDownloads(data.downloads || []);
      } catch (e) {
        setErr(e?.response?.data?.detail || "Link expired or invalid");
      } finally { setLoading(false); }
    })();
  }, [purchaseId, token]);

  if (loading) return <div className="min-h-screen bg-black text-white flex items-center justify-center px-6 text-center">{purchaseId === "pending" ? pendingHint : "Loading your purchase…"}</div>;
  if (err) return <div className="min-h-screen bg-black text-white flex items-center justify-center px-6 text-center max-w-md mx-auto">{err}<br/><button className="mt-4 underline" onClick={() => nav("/beats")}>Back to store</button></div>;

  return (
    <div className="min-h-screen text-white" style={{ background: "radial-gradient(circle at 50% -10%, rgba(34,197,94,0.10), #000 60%)" }}>
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
        <div className="mb-4"><GlobalNav /></div>
        <div className="rounded-3xl bg-[#0a0a0a] border border-emerald-400/40 p-6 sm:p-8 space-y-5" data-testid="purchase-success-card">
          <div className="flex items-center gap-3">
            <CheckCircle size={28} weight="fill" className="text-emerald-400" />
            <div>
              <div className="font-black text-2xl sm:text-3xl leading-tight">Your beat is ready</div>
              <div className="text-white/60 text-sm">Preview each file, download, and read the license.</div>
            </div>
          </div>
          <div className="rounded-2xl bg-black/40 border border-white/10 p-4 flex flex-wrap items-baseline justify-between gap-2">
            <div className="min-w-0">
              <div className="font-mono font-bold text-xl truncate">"{purchase.beat_name}"</div>
              <div className="text-xs text-white/50 truncate">{purchase.artist_tag} · {purchase.tier_label}</div>
            </div>
            <div className="text-emerald-300 font-black">${((purchase.price_cents || 0) / 100).toFixed(0)}</div>
          </div>

          <div>
            <div className="text-xs uppercase tracking-wide text-white/40 mb-2">Your files</div>
            <div className="space-y-2" data-testid="purchase-downloads">
              {downloads.map((d) => (
                <DownloadRow key={d.kind} d={d} />
              ))}
            </div>
            <p className="mt-3 text-[11px] text-white/40">Links expire on {purchase.download_expires_at?.slice(0, 10)}. Save your files locally. A copy of the license is also attached to your email at <span className="text-white/60">{purchase.buyer_email}</span>.</p>
          </div>

          {purchase.auto_account_created ? (
            <div className="rounded-2xl bg-emerald-500/10 border border-emerald-400/30 p-4 text-sm">
              We created a free C.R.O.W. account for you with 2 beat generations to try. Check your inbox to set a password — or set it later from your library.
            </div>
          ) : null}

          <button
            onClick={() => {
              try { sessionStorage.setItem("crow_post_purchase", JSON.stringify({ beat_name: purchase.beat_name, artist_tag: purchase.artist_tag })); } catch {}
              nav("/?post_purchase=1");
            }}
            className="w-full py-3 rounded-full font-black text-sm sm:text-base bg-emerald-500 hover:bg-emerald-400 text-black flex items-center justify-center gap-2"
            data-testid="post-purchase-upsell"
          >
            <Sparkle size={18} weight="fill" /> Make your own beat in this style <ArrowRight size={18} weight="bold" />
          </button>
        </div>
      </div>
    </div>
  );
}
